import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const BrandContext = createContext(null);

export const BrandProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dataFetched, setDataFetched] = useState(false);
  
  // Fetch brands when user is authenticated - only once
  useEffect(() => {
    if (isAuthenticated && !dataFetched) {
      fetchBrands();
    } else if (!isAuthenticated) {
      setBrands([]);
      setSelectedBrand(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);
  
  // Fetch brands from API
  const fetchBrands = async () => {
    // Prevent multiple simultaneous fetches
    if (loading) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('You need to log in again.');
        setLoading(false);
        return;
      }
      
      const response = await axios.get('/api/brands', {
        headers: { 'x-auth-token': token }
      });
      
      setBrands(response.data);
      setDataFetched(true);

      // Restore previously selected brand from localStorage, or select first one
      if (response.data.length > 0 && !selectedBrand) {
        const savedBrandId = localStorage.getItem('selectedBrandId');
        if (savedBrandId) {
          // Find the saved brand in the list
          const savedBrand = response.data.find(b => String(b.id) === String(savedBrandId));
          if (savedBrand) {
            setSelectedBrand(savedBrand);
          } else {
            // Saved brand not found, select first one
            setSelectedBrand(response.data[0]);
          }
        } else {
          // No saved brand, select first one
          setSelectedBrand(response.data[0]);
        }
      }
      
    } catch (error) {
      console.error('Error fetching brands:', error);
      if (error.response && error.response.status === 401) {
        setError('Your session has expired. Please log in again.');
      } else {
        setError('Failed to fetch brands. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Select a brand
  const selectBrand = (brand) => {
    setSelectedBrand(brand);
    // You could save the selection to localStorage for persistence across page refreshes
    localStorage.setItem('selectedBrandId', brand.id);
  };
  
  // Create a new brand
  const createBrand = async (brandData) => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/brands', brandData, {
        headers: { 'x-auth-token': token }
      });
      
      // Add the new brand to the list
      setBrands(prevBrands => [...prevBrands, response.data]);
      
      // Select the new brand
      setSelectedBrand(response.data);
      
      return { success: true, brand: response.data };
    } catch (error) {
      console.error('Error creating brand:', error);
      setError(error.response?.data?.message || 'Failed to create brand.');
      return { success: false, error: error.response?.data?.message || 'Creation failed' };
    } finally {
      setLoading(false);
    }
  };
  
  // Update a brand
  const updateBrand = async (brandId, brandData) => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      const response = await axios.put(`/api/brands/${brandId}`, brandData, {
        headers: { 'x-auth-token': token }
      });
      
      // Update the brand in the list
      setBrands(prevBrands => 
        prevBrands.map(brand => 
          brand.id === brandId ? response.data : brand
        )
      );
      
      // Update selected brand if it's the one being updated
      if (selectedBrand && selectedBrand.id === brandId) {
        setSelectedBrand(response.data);
      }
      
      return { success: true, brand: response.data };
    } catch (error) {
      console.error('Error updating brand:', error);
      setError(error.response?.data?.message || 'Failed to update brand.');
      return { success: false, error: error.response?.data?.message || 'Update failed' };
    } finally {
      setLoading(false);
    }
  };
  
  // Delete a brand
  const deleteBrand = async (brandId) => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      await axios.delete(`/api/brands/${brandId}`, {
        headers: { 'x-auth-token': token }
      });
      
      // Remove the brand from the list
      const updatedBrands = brands.filter(brand => brand.id !== brandId);
      setBrands(updatedBrands);
      
      // If the deleted brand was selected, select another brand if available
      if (selectedBrand && selectedBrand.id === brandId) {
        setSelectedBrand(updatedBrands.length > 0 ? updatedBrands[0] : null);
      }
      
      return { success: true };
    } catch (error) {
      console.error('Error deleting brand:', error);
      setError(error.response?.data?.message || 'Failed to delete brand.');
      return { success: false, error: error.response?.data?.message || 'Deletion failed' };
    } finally {
      setLoading(false);
    }
  };
  
  // Get brand users
  const getBrandUsers = async (brandId) => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/brands/${brandId}/users`, {
        headers: { 'x-auth-token': token }
      });
      
      return { success: true, users: response.data };
    } catch (error) {
      console.error('Error fetching brand users:', error);
      setError(error.response?.data?.message || 'Failed to fetch brand users.');
      return { success: false, error: error.response?.data?.message || 'Fetch failed' };
    } finally {
      setLoading(false);
    }
  };
  
  // Check if user has access to a specific brand
  const hasAccessToBrand = (brandId) => {
    return brands.some(brand => brand.id === brandId);
  };
  
  // Force a refresh only if not already loading
  const manualRefresh = () => {
    if (!loading) {
      setDataFetched(false);
      fetchBrands();
    }
  };
  
  return (
    <BrandContext.Provider value={{
      brands,
      selectedBrand,
      loading,
      error,
      selectBrand,
      createBrand,
      updateBrand,
      deleteBrand,
      getBrandUsers,
      hasAccessToBrand,
      refreshBrands: manualRefresh
    }}>
      {children}
    </BrandContext.Provider>
  );
};

export const useBrand = () => {
  const context = useContext(BrandContext);
  if (!context) {
    throw new Error('useBrand must be used within a BrandProvider');
  }
  return context;
};

export default BrandContext;